import { useQuery } from 'urql';

import {
  SKILL_TAXONOMIES_VERSIONS_QUERY,
  type SkillTaxonomiesVersionsQuery,
} from '@revelio/data-access';

import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';

type SkillTaxonomy = NonNullable<
  SkillTaxonomiesVersionsQuery['selectionList']
>['skill_taxonomies']['taxs'][0];

export const useSkillTaxonomyVersions = () => {
  const [{ data, fetching, error }] = useQuery({
    query: SKILL_TAXONOMIES_VERSIONS_QUERY,
  });

  return {
    taxonomies: data?.selectionList?.skill_taxonomies?.taxs,
    loading: fetching,
    error,
  };
};

export const useCurrentSkillTaxonomyVersion = () => {
  const { taxonomies, loading, error } = useSkillTaxonomyVersions();

  const defaultVersion = (() => {
    const latestDefaultTaxonomy = taxonomies?.find(
      (taxonomy: SkillTaxonomy) => taxonomy.is_latest
    );
    if (latestDefaultTaxonomy) return latestDefaultTaxonomy.version;

    return undefined;
  })();

  const getSpecificTaxonomyVersion = (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => {
    if (taxonomySelection.taxonomyId) {
      const taxonomy = taxonomies?.find(
        (taxonomy: SkillTaxonomy) =>
          taxonomy.id === taxonomySelection.taxonomyId
      );
      return taxonomy?.version;
    }

    return defaultVersion;
  };

  return {
    version: defaultVersion,
    taxonomies,
    loading: loading || (!taxonomies && !error),
    error,
    isValidTaxonomyId: (taxonomyId: number) => {
      if (error) {
        console.error('Taxonomy versions are unknown', error);
        return false;
      }
      return (
        taxonomies?.some(
          (taxonomy: SkillTaxonomy) => taxonomy.id === taxonomyId
        ) ?? false
      );
    },
    getSpecificTaxonomyVersion,
  };
};
